import { createTheme, ThemeProvider } from "@mui/material/styles";

import shadows from "./shadows";
import palette from "./palette";

const breakpoints = {
  values: {
    xs: 0,
    sm: 600,
    md: 1000,
    lg: 1200,
    xl: 1920,
  },
};

const components = {
  MuiButton: {
    defaultProps: {
      disableElevation: true,
    },
    styleOverrides: {
      root: {
        textTransform: "none",
        borderRadius: "4px",
      },
      sizeSmall: {
        padding: "6px 16px",
      },
      sizeMedium: {
        padding: "8px 20px",
      },
      sizeLarge: {
        padding: "11px 24px",
      },
      textSizeSmall: {
        padding: "7px 12px",
      },
      textSizeMedium: {
        padding: "9px 16px",
      },
      textSizeLarge: {
        padding: "12px 16px",
      },
    },
  },
  MuiButtonBase: {
    defaultProps: {
      disableRipple: true,
    },
  },
  MuiCardContent: {
    styleOverrides: {
      root: {
        padding: "32px 24px",
        "&:last-child": {
          paddingBottom: "32px",
        },
      },
    },
  },
  MuiCardHeader: {
    defaultProps: {
      titleTypographyProps: {
        variant: "h6",
      },
      subheaderTypographyProps: {
        variant: "body2",
      },
    },
    styleOverrides: {
      root: {
        padding: "32px 24px",
      },
    },
  },
  MuiCssBaseline: {
    styleOverrides: {
      "*": {
        boxSizing: "border-box",
        margin: 0,
        padding: 0,
      },
      html: {
        MozOsxFontSmoothing: "grayscale",
        WebkitFontSmoothing: "antialiased",
        display: "flex",
        flexDirection: "column",
        minHeight: "100%",
        width: "100%",
      },
      body: {
        display: "flex",
        flex: "1 1 auto",
        flexDirection: "column",
        minHeight: "100%",
        width: "100%",
      },
      "#__next": {
        display: "flex",
        flex: "1 1 auto",
        flexDirection: "column",
        height: "100%",
        width: "100%",
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      notchedOutline: {
        borderColor: "#E6E8F0",
      },
    },
  },
  MuiTableHead: {
    styleOverrides: {
      root: {
        backgroundColor: "#F3F4F6",
        ".MuiTableCell-root": {
          color: "#374151",
        },
        borderBottom: "none",
        "& .MuiTableCell-root": {
          borderBottom: "none",
          fontSize: "12px",
          fontWeight: 600,
          lineHeight: 1,
          letterSpacing: 0.5,
          textTransform: "uppercase",
        },
        "& .MuiTableCell-paddingCheckbox": {
          paddingTop: 4,
          paddingBottom: 4,
        },
      },
    },
  },
  MuiDataGrid: {
    styleOverrides: {
      root: {
        ".MuiDataGrid-columnHeaderTitle": {
          overflow: "hidden",
          lineHeight: "20px",
          whiteSpace: "normal",
        },
        ".MuiDataGrid-columnHeaders": {
          backgroundColor: "#E5E7EB",
          borderRadius: 0,
        },
      },
    },
  },
};

const shape = {
  borderRadius: 8,
};

const typography = {
  button: {
    fontWeight: 600,
  },
  fontFamily:
    '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji"',
  body1: {
    fontSize: "1rem",
    fontWeight: 400,
    lineHeight: 1.5,
  },
  body2: {
    fontSize: "0.875rem",
    fontWeight: 400,
    lineHeight: 1.57,
  },
  subtitle1: {
    fontSize: "1rem",
    fontWeight: 500,
    lineHeight: 1.75,
  },
  subtitle2: {
    fontSize: "0.875rem",
    fontWeight: 500,
    lineHeight: 1.57,
  },
  overline: {
    fontSize: "0.75rem",
    fontWeight: 600,
    letterSpacing: "0.5px",
    lineHeight: 2.5,
    textTransform: "uppercase",
  },
  caption: {
    fontSize: "0.75rem",
    fontWeight: 400,
    lineHeight: 1.66,
  },
  h1: {
    fontWeight: 700,
    fontSize: "3.5rem",
    lineHeight: 1.375,
  },
  h2: {
    fontWeight: 700,
    fontSize: "3rem",
    lineHeight: 1.375,
  },
  h3: {
    fontWeight: 700,
    fontSize: "2.25rem",
    lineHeight: 1.375,
  },
  h4: {
    fontWeight: 700,
    fontSize: "2rem",
    lineHeight: 1.375,
  },
  h5: {
    fontWeight: 600,
    fontSize: "1.5rem",
    lineHeight: 1.375,
  },
  h6: {
    fontWeight: 600,
    fontSize: "1.125rem",
    lineHeight: 1.375,
  },
};

const lightTheme = createTheme({
  palette,
  breakpoints,
  components,
  shape,
  shadows: shadows(),
  typography,
});

export { ThemeProvider, lightTheme };
